<template>
  <div class="login-container">
    <div class="login-card">
      <div class="login-header">
        <h1>Libro Admin</h1>
        <p>Inicia sesión en tu cuenta</p>
      </div>

      <div class="tab-content">
        <div v-if="!googleLoading" class="google-login-content">
          <p class="google-login-info">Inicia sesión con tu cuenta Google autorizada</p>
          <div id="google-signin-button" class="google-button-container"></div>
        </div>
        <div v-else class="google-loading">
          <div class="spinner"></div>
          <p>Iniciando sesión con Google...</p>
        </div>
      </div>

      <p v-if="error" class="error-banner">{{ error }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import '../types/google.d.ts';

const router = useRouter();
const authStore = useAuthStore();

const error = ref('');
const googleLoading = ref(false);

const loadGoogleScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.google) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Sign-In script'));
    document.head.appendChild(script);
  });
};

const initializeGoogleSignIn = () => {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!clientId) {
    console.error('[Google Login] VITE_GOOGLE_CLIENT_ID is not set');
    error.value = 'El inicio de sesión con Google no está configurado';
    return;
  }

  if (!window.google) {
    console.error('[Google Login] Google Identity Services not loaded');
    return;
  }

  window.google.accounts.id.initialize({
    client_id: clientId,
    callback: handleGoogleCallback,
    auto_select: false,
    cancel_on_tap_outside: true,
  });

  const buttonContainer = document.getElementById('google-signin-button');
  if (buttonContainer) {
    window.google.accounts.id.renderButton(
      buttonContainer,
      {
        theme: 'filled_black',
        size: 'large',
        width: 300,
        text: 'signin_with',
        shape: 'rectangular',
      }
    );
  }
};

const handleGoogleCallback = async (response: { credential: string }) => {
  error.value = '';
  googleLoading.value = true;

  try {
    await authStore.googleLogin(response.credential);
    router.push('/');
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Error al iniciar sesión con Google';
    error.value = message;
  } finally {
    googleLoading.value = false;
  }
};

onMounted(async () => {
  try {
    await loadGoogleScript();
    initializeGoogleSignIn();
  } catch {
    error.value = 'Error al cargar Google Sign-In. Actualiza la página e intenta nuevamente.';
  }
});
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0d0d0d;
  padding: 20px;
}

.login-card {
  background-color: #1a1a1a;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  padding: 40px;
  width: 100%;
  max-width: 450px;
}

.login-header {
  text-align: center;
  margin-bottom: 25px;
}

.login-header h1 {
  color: #4a90d9;
  font-size: 32px;
  font-weight: bold;
  margin: 0 0 10px 0;
}

.login-header p {
  color: #b0b0b0;
  font-size: 16px;
  margin: 0;
}

.tab-content {
  min-height: 200px;
}

.google-login-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.google-login-info {
  color: #b0b0b0;
  font-size: 14px;
  margin-bottom: 30px;
  text-align: center;
}

.google-button-container {
  display: flex;
  justify-content: center;
}

.google-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.google-loading p {
  color: #b0b0b0;
  margin-top: 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #3a3a3a;
  border-top-color: #4a90d9;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-banner {
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid #ff6b6b;
  border-radius: 6px;
  color: #ff6b6b;
  padding: 12px;
  margin: 20px 0 0 0;
  font-size: 14px;
  text-align: center;
}
</style>
